import { useMemo } from 'react';
import type { ApiResponse } from 'vopay';
import type { RolePayload, UsersHostProps } from 'shared/users';
import { api } from '@/services/api.config';
import { useClientData } from '@/providers/ClientData.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import useSubAccountsEnabled from '@/hooks/useSubAccountsEnabled';
import { propagateRoleDeleteToSubAccounts } from './UserRoles/propagateRoleDeleteToSubAccounts';
import { propagateRoleToSubAccounts } from './UserRoles/propagateRoleToSubAccounts';
import { propagateRoleUpdateToSubAccounts } from './UserRoles/propagateRoleUpdateToSubAccounts';

type UseUsersHostPropsOptions = {
  title?: string;
  /** Scopes every read and write to this sub-account instead of the logged-in account. */
  bearer?: string;
};

// The shared feature expects every call to reject on failure, `Success: false` included. The api layer
// resolves those, so this turns them into rejections without logging. For callers that log the rejection
// themselves, like the sub-account fan-out helpers.
async function ensureSuccess<T extends ApiResponse>(request: Promise<T>): Promise<T> {
  const res = await request;

  if (!res.Success) throw new Error(res.ErrorMessage);

  return res;
}

// `ensureSuccess` that also logs once, as the Direct Pattern hooks do.
async function unwrap<T extends ApiResponse>(request: Promise<T>): Promise<T> {
  try {
    return await ensureSuccess(request);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// The role endpoints take the permission keys as one comma-separated string.
function toRoleRequest(role: RolePayload) {
  return { ...role, Permissions: role.Permissions.join(',') };
}

// The one place client-dashboard maps its own user/account/permission model onto what the shared Users
// feature takes as props. partner-dashboard writes its own equivalent from its own providers. Every
// endpoint the feature calls is the same one for every host (scoped by bearer) and goes through shared's
// own useApi() — only the sub-account role fan-out below is genuine host-specific orchestration.
export default function useUsersHostProps({ title, bearer }: UseUsersHostPropsOptions = {}): UsersHostProps {
  const {
    response: { LoginUserName },
  } = useClientData();

  const {
    userSettings: { timezone },
  } = useUserSettingsProvider();

  const {
    permissions: { AddUsers, ManageUserPermissions, ViewUserRoles, ManageUserRoles },
  } = usePermissions();

  const hasSubAccounts = useSubAccountsEnabled();

  // Role fan-out across sub-accounts (create/update/delete/count); undefined when the account has none.
  const subAccountFns = useMemo<Pick<
    UsersHostProps,
    'createRoleInSubAccounts' | 'updateRoleInSubAccounts' | 'countSubAccounts' | 'deleteRoleFromSubAccounts'
  > | null>(() => {
    if (!hasSubAccounts) return null;

    // Sub-accounts of the scoped account (in sub-account context, that sub-account's own children).
    const fetchSubAccounts = (params: Parameters<typeof api.fetchSubAccounts>[0]) =>
      api.fetchSubAccounts(params, undefined, bearer);

    const deleteRoleIn = (params: { RoleID: number }, roleBearer?: string) =>
      api.deleteAccountUserRole(params, undefined, roleBearer);

    const subAccountRoleDeps = {
      fetchSubAccountsAsync: fetchSubAccounts,
      fetchAccountUserRolesWithBearer: (childBearer: string) => api.fetchAccountUserRoles(childBearer),
    };

    return {
      countSubAccounts: async () =>
        (await unwrap(fetchSubAccounts({ ShowToken: true, ActiveOnly: true }))).Subaccounts.length,
      deleteRoleFromSubAccounts: (roleName) =>
        propagateRoleDeleteToSubAccounts(roleName, {
          ...subAccountRoleDeps,
          deleteUserRole: (params, childBearer) => ensureSuccess(deleteRoleIn(params, childBearer)),
        }),
      createRoleInSubAccounts: (role) =>
        propagateRoleToSubAccounts(toRoleRequest(role), {
          fetchSubAccountsAsync: fetchSubAccounts,
          createUserRole: (params, childBearer) =>
            ensureSuccess(api.postCreateAccountUserRole(params, undefined, childBearer)),
        }),
      updateRoleInSubAccounts: (role, options) =>
        propagateRoleUpdateToSubAccounts(
          toRoleRequest(role),
          {
            ...subAccountRoleDeps,
            updateUserRole: (params, childBearer) =>
              ensureSuccess(api.postUpdateAccountUserRole(params, undefined, childBearer)),
          },
          options,
        ),
    };
  }, [bearer, hasSubAccounts]);

  return useMemo(
    () => ({
      title,
      viewer: { username: LoginUserName, timezone },
      account: { bearer, hasSubAccounts },
      userPermissions: {
        canAddUsers: !!AddUsers,
        canManageUserPermissions: !!ManageUserPermissions,
        canViewRoles: !!ViewUserRoles,
        canManageRoles: !!ManageUserRoles,
      },
      ...subAccountFns,
    }),
    [
      title,
      LoginUserName,
      timezone,
      bearer,
      hasSubAccounts,
      AddUsers,
      ManageUserPermissions,
      ViewUserRoles,
      ManageUserRoles,
      subAccountFns,
    ],
  );
}
